import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Factory } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import centroTrabalhoService, { type CentroTrabalho } from '@/services/centroTrabalho';

export default function CentroTrabalho() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CentroTrabalho[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await centroTrabalhoService.listar();
      setItems(data);
    } catch (error) {
      console.error('Erro ao carregar centros de trabalho', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtrados = useMemo(() => {
    let resultado = items;
    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter(
        (c) => c.codigo?.toLowerCase().includes(termo) || c.descricao?.toLowerCase().includes(termo)
      );
    }
    if (filtroAtivo !== '') {
      resultado = resultado.filter((c) => c.ativo === (filtroAtivo === 'true'));
    }
    return resultado;
  }, [items, busca, filtroAtivo]);

  const deletar = async (item: CentroTrabalho) => {
    try {
      await centroTrabalhoService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir centro de trabalho', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Factory size={21} strokeWidth={2} />}
        title="Centros de Trabalho"
        description="Cadastro de centros de trabalho utilizados nos processos de industrialização."
        actions={
          <Button type="button" onClick={() => navigate('/producao/centro-trabalho/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Centro de Trabalho
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar centro de trabalho..."
                />
              </div>
              <Select filter value={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.value)}>
                <option value="">Todos</option>
                <option value="true">Ativos</option>
                <option value="false">Inativos</option>
              </Select>
            </div>
          }
        />

        {!loading && filtrados.length === 0 ? (
          <EmptyState message="Nenhum centro de trabalho encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Tipo</th>
                  <th>Capacidade (h)</th>
                  <th>Custo/Hora</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  filtrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.codigo}</td>
                      <td>{item.descricao}</td>
                      <td>{item.tipo}</td>
                      <td>{item.capacidade_hora ?? '-'}</td>
                      <td>{item.custo_hora != null ? `R$ ${Number(item.custo_hora).toFixed(2)}` : '-'}</td>
                      <td>
                        <StatusBadge label={item.ativo ? 'Ativo' : 'Inativo'} tone={item.ativo ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <TableActions
                          onEdit={() => navigate(`/producao/centro-trabalho/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          deleteTitle="Excluir centro de trabalho"
                          deleteDescription={`Tem certeza de que deseja excluir o centro de trabalho "${item.descricao}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
